import React, {useMemo} from "react";
import {AgGridReact} from "ag-grid-react";
import {Card} from "../../../../../components/card/card.jsx";
import StatusCell from "../../../utils/components/status-cell.jsx";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import projectUtils from "../../../../../utils/project-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../../utils/ag-grid-columns-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";

const DEFAULT_COL_DEF = {...DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL, sortable: true};

const NavigationLink = ({to, children}) => {
    const navigate = navigateHook();

    const onClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        navigate(to.dev, true);
    };

    return (
        <button type="button" className="cursor-pointer text-blue-700" onClick={onClick}>
            {children}
        </button>
    );
};

const TasksGrid = ({rows, dispatchUrl}) => {
    const columnDefs = useMemo(() => [
        {field: "dtCreated", headerName: "Data/Hora criação", width: 160, comparator: aggridUtils.agGridCompareDateTime},
        {field: "dateToCreate", headerName: "Data/Hora agendamento", width: 160, comparator: aggridUtils.agGridCompareDateTime},
        {field: "statusText", headerName: "Status", width: 140,
            cellRenderer: ({data}) => <StatusCell active={!!data.status}/>,
        },
        {field: "reasonText", headerName: "Natureza", flex: 1, wrapText: true, autoHeight: true},
        {field: "numberCheckbook", headerName: "Despacho", width: 130, comparator: aggridUtils.agGridNumberYearComparator,
            cellRenderer: ({data}) => data.dispatchId ? <NavigationLink to={dispatchUrl(data.dispatchId)}>{data.numberCheckbook}</NavigationLink>: "",
        },
        {field: "cancelObservation", headerName: "Motivo encerramento", flex: 1, wrapText: true, autoHeight: true},
    ], [dispatchUrl]);

    return (
        <AgGridReact
            domLayout="autoHeight"
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={DEFAULT_COL_DEF}
            animateRows={false}
            suppressCellFocus={true}
            gridOptions={{localeText: aggridUtils.localization()}}
            theme={aggridUtils.generalTheme()}
        />
    );
};

const DispatchesGrid = ({rows, boName, dispatchUrl, occurrenceUrl}) => {
    const columnDefs = useMemo(() => [
        {field: "numberCheckbook", headerName: "Talão", width: 150, comparator: aggridUtils.agGridNumberYearComparator,
            cellRenderer: ({data}) => data.id ? <NavigationLink to={dispatchUrl(data.id)}>{data.numberCheckbook ?? "Sem talão"}</NavigationLink> : "",
        },
        {field: "occurrenceNumber", headerName: boName, width: 150, comparator: aggridUtils.agGridNumberYearComparator,
            cellRenderer: ({data}) => data.occurrenceNumber ? <NavigationLink to={occurrenceUrl(data.occurrenceNumber)}>{data.occurrenceNumber}</NavigationLink> : "",
        },
        {field: "start", headerName: "Início", width: 200, comparator: aggridUtils.agGridCompareDateTime,
            valueGetter: ({data}) => projectUtils.formatDate(data.start)},
        {field: "end", headerName: "Fim", width: 200, comparator: aggridUtils.agGridCompareDateTime,
            valueGetter: ({data}) => projectUtils.formatDate(data.end)},
        {field: "nature", headerName: "Natureza", flex: 1, wrapText: true, autoHeight: true},
        {headerName: "Guarnição", flex: 2, valueGetter: ({data}) => data.garrison?.name ?? ""},
        {headerName: "Status", width: 140,
            cellRenderer: ({data}) => <StatusCell active={!!data.status}/>,
        },
    ], [boName, dispatchUrl, occurrenceUrl]);

    return (
        <AgGridReact
            domLayout="autoHeight"
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={DEFAULT_COL_DEF}
            animateRows={false}
            suppressCellFocus={true}
            gridOptions={{localeText: aggridUtils.localization()}}
            theme={aggridUtils.generalTheme()}
        />
    );
};

const AttendanceRelatedTables = ({taskList = [], dispatchesList = [], boName, dispatchUrl, occurrenceUrl}) => (
    <>
        {!!taskList.length && (
            <Card>
                <Card.Header>
                    <Card.Title>Tarefas</Card.Title>
                </Card.Header>
                <Card.Body>
                    <TasksGrid rows={taskList} dispatchUrl={dispatchUrl}/>
                </Card.Body>
            </Card>
        )}

        {!!dispatchesList.length && (
            <Card>
                <Card.Header>
                    <Card.Title>Despachos</Card.Title>
                </Card.Header>
                <Card.Body>
                    <DispatchesGrid rows={dispatchesList} boName={boName} dispatchUrl={dispatchUrl} occurrenceUrl={occurrenceUrl}/>
                </Card.Body>
            </Card>
        )}
    </>
);

export default AttendanceRelatedTables;
